import type { ClubView } from "../game/types";
import { definirPreferencia, usePreferencia } from "../preferencias";
import { Panel } from "../ui/atoms";
import { conquistaDoMotor, useT } from "../i18n";

/**
 * Carreira do treinador.
 *
 * É a única tela do jogo que não fala do clube de agora. Depois de dez
 * temporadas, o que sobra de uma carreira é isto: por onde passou, onde
 * terminou e o que levantou. Nem Elifoot nem Brasfoot guardavam essa memória, e
 * é justamente ela que faz o jogador querer a próxima temporada.
 */
export function CareerPanel({
  view,
  onExportPack,
  onSaveNow,
  busy,
}: {
  view: ClubView;
  onExportPack: () => void;
  /**
   * Salvar agora. Nulo na carreira online, que salva sempre: é do save que ela
   * sobe para o servidor, e desligar ali perderia rodadas.
   */
  onSaveNow: (() => void) | null;
  busy: boolean;
}) {
  const t = useT();
  const salvaSozinho = usePreferencia("salvamentoAutomatico");
  const { career } = view;
  const ganhas = career.achievements.filter((entrada) => entrada.season !== null);
  const faltam = career.achievements.filter((entrada) => entrada.season === null);

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Panel
        title={career.managerName}
        action={
          <span className="label-muted">
            {t("carreira.resumo", {
              temporadas: career.seasons.length,
              titulos: career.titles,
              reputacao: Math.round(career.reputation),
            })}
          </span>
        }
      >
        {career.seasons.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            {t("carreira.carreiraComeca")}
          </p>
        ) : (
          <div className="scroll" style={{ maxHeight: "40vh" }}>
            <table>
              <thead>
                <tr>
                  <th className="right" style={{ width: 60 }}>
                    {t("carreira.colunas.temporada")}
                  </th>
                  <th>{t("carreira.colunas.clube")}</th>
                  <th>{t("carreira.colunas.competicao")}</th>
                  <th className="right" style={{ width: 70 }}>
                    {t("carreira.colunas.posicao")}
                  </th>
                  <th>{t("carreira.colunas.titulos")}</th>
                </tr>
              </thead>
              <tbody>
                {[...career.seasons].reverse().map((temporada) => (
                  <tr key={temporada.season} className="player">
                    <td className="right num" style={{ color: "var(--text-dim)" }}>
                      {temporada.season}
                    </td>
                    <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{temporada.clubName}</td>
                    <td className="muted" style={{ fontSize: 11.5, whiteSpace: "nowrap" }}>
                      {temporada.competitionName}
                    </td>
                    <td
                      className="right num"
                      style={{
                        fontWeight: 700,
                        color: temporada.position === 1 ? "var(--accent)" : "inherit",
                      }}
                    >
                      {t("carreira.posicaoValor", { posicao: temporada.position })}
                    </td>
                    <td style={{ fontSize: 11.5, color: "var(--accent)" }}>
                      {temporada.titleNames.join(" · ") || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {onSaveNow && (
        <Panel title={t("carreira.salvamento")}>
          <div
            className="row"
            style={{ gap: 12, justifyContent: "space-between", flexWrap: "wrap", minHeight: 38 }}
          >
            <label className="row" style={{ gap: 10 }}>
              <input
                type="checkbox"
                role="switch"
                checked={salvaSozinho}
                onChange={(evento) => definirPreferencia("salvamentoAutomatico", evento.target.checked)}
              />
              <span style={{ fontWeight: 600 }}>{t("carreira.salvarSozinho")}</span>
            </label>
            {!salvaSozinho && (
              <button className="primary" onClick={onSaveNow} disabled={busy}>
                {t("carreira.salvarAgora")}
              </button>
            )}
          </div>
        </Panel>
      )}

      <Panel
        title={t("carreira.bancoDeDados")}
        action={<span className="label-muted">{view.club.leagueName}</span>}
      >
        <p className="muted" style={{ margin: "0 0 10px", fontSize: 12 }}>
          {t("carreira.explicacaoDoBanco")}
        </p>
        <button className="ghost" onClick={onExportPack}>
          {t("carreira.salvarBanco")}
        </button>
      </Panel>

      <Panel
        title={t("carreira.conquistas")}
        action={
          <span className="label-muted">
            {t("carreira.conquistasResumo", {
              feitas: ganhas.length,
              total: career.achievements.length,
            })}
          </span>
        }
      >
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 10 }}
        >
          {[...ganhas, ...faltam].map((conquista) => {
            const feita = conquista.season !== null;
            // O motor manda a chave; o nome e a descricao sao deste dicionario,
            // e e por isso que a conquista ganha ha dez temporadas aparece no
            // idioma de quem esta lendo hoje.
            const { nome, descricao } = conquistaDoMotor(conquista.key);
            return (
              <div
                key={conquista.id}
                style={{
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: feita ? "var(--primary-glow)" : "var(--surface-2)",
                  border: feita ? "1px solid var(--primary)" : "1px solid var(--border)",
                  opacity: feita ? 1 : 0.55,
                }}
              >
                <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
                  <strong style={{ fontSize: 13 }}>{nome}</strong>
                  {conquista.season !== null && (
                    <span className="label">
                      {t("carreira.temporadaDaConquista", { temporada: conquista.season })}
                    </span>
                  )}
                </div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                  {descricao}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
